import { useNavigate } from "react-router-dom";
import { Building2, ClipboardCheck, Wrench, Phone, Mail, MessageCircle, ArrowRight, Shield, Clock, Users } from "lucide-react";
import { Button } from "../components/ui/button";

export default function LandingPage() {
  const navigate = useNavigate();
  const whatsappNumber = "523331912529";
  const whatsappLink = `https://wa.me/${whatsappNumber}`;

  const services = [
    {
      icon: Building2,
      title: "Administración y Gestión de Rentas",
      description: "Nos encargamos de todo el proceso de arrendamiento, desde la búsqueda de inquilinos hasta la firma de contratos y seguimiento mensual.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: ClipboardCheck,
      title: "Cobranza",
      description: "Sistema profesional de cobranza con seguimiento puntual, recordatorios automáticos y reportes detallados para propietarios.",
      color: "from-emerald-500 to-emerald-600"
    },
    {
      icon: Wrench,
      title: "Mantenimiento",
      description: "Coordinamos reparaciones y mantenimiento preventivo con proveedores de confianza, cuidando tu inversión.",
      color: "from-amber-500 to-amber-600"
    }
  ];

  const features = [
    { icon: Shield, text: "Seguridad y confianza" },
    { icon: Clock, text: "Atención 24/7" },
    { icon: Users, text: "+10 años de experiencia" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/logo-rotco.png" 
                alt="Rotco Propiedades" 
                className="h-10 sm:h-12 w-auto"
              />
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-slate-800">Rotco Propiedades</h1>
                <p className="text-xs text-slate-500">Administración Inmobiliaria</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate("/login")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 sm:px-6 py-2 rounded-full shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30"
              data-testid="login-button"
            >
              <span className="hidden sm:inline">Ingresar al Sistema</span>
              <span className="sm:hidden">Ingresar</span>
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
                Administración Profesional
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
                Tu propiedad en las
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500"> mejores manos</span>
              </h2>
              
              <p className="text-base sm:text-lg text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0">
                Somos expertos en administración inmobiliaria. Maximizamos el rendimiento de tu inversión mientras tú te olvidas de las preocupaciones.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  onClick={() => window.open(whatsappLink, "_blank")}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-3 rounded-full shadow-lg shadow-emerald-500/25 transition-all hover:shadow-xl hover:scale-105"
                  data-testid="whatsapp-hero-button"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Contáctanos por WhatsApp
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/login")}
                  className="border-2 border-slate-300 hover:border-blue-500 hover:text-blue-600 font-semibold px-8 py-3 rounded-full transition-all"
                  data-testid="login-hero-button"
                >
                  Ya soy cliente
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mt-10 pt-8 border-t border-slate-200">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-slate-600">
                    <feature.icon className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mascot */}
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-emerald-400 rounded-full blur-3xl opacity-20 scale-90"></div>
                <img 
                  src="/assets/mascota-rotco.png" 
                  alt="Mascota Rotco" 
                  className="relative w-64 sm:w-80 lg:w-96 h-auto drop-shadow-2xl animate-float"
                  data-testid="mascot-image"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-200 rounded-full blur-2xl opacity-50"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-emerald-200 rounded-full blur-3xl opacity-50"></div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-24 bg-white" id="servicios">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">
              Nuestros Servicios
            </h3>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Todo lo que necesitas para tu propiedad
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Ofrecemos soluciones integrales para que tu inversión inmobiliaria sea rentable y libre de complicaciones.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group relative bg-white rounded-2xl p-6 sm:p-8 shadow-lg shadow-slate-200/50 border border-slate-100 hover:shadow-xl hover:shadow-slate-200/80 transition-all duration-300 hover:-translate-y-1"
                data-testid={`service-card-${index}`}
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} text-white shadow-lg mb-6 group-hover:scale-110 transition-transform`}>
                  <service.icon className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6">
            ¿Listo para dejar tu propiedad en buenas manos?
          </h2>
          <p className="text-lg text-blue-100 mb-10 max-w-2xl mx-auto">
            Contáctanos hoy y descubre cómo podemos ayudarte a maximizar el rendimiento de tu inversión inmobiliaria.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.open(whatsappLink, "_blank")}
              className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-4 rounded-full shadow-xl transition-all hover:scale-105"
              data-testid="whatsapp-cta-button"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              WhatsApp: 33 3191 2529
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = "mailto:fernandoac@alarmen.com.mx"}
              className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-full transition-all"
              data-testid="email-cta-button"
            >
              <Mail className="mr-2 h-5 w-5" />
              Enviar correo
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-12 sm:py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="text-center md:text-left">
              <img 
                src="/assets/logo-rotco.png" 
                alt="Rotco Propiedades" 
                className="h-12 w-auto mx-auto md:mx-0 mb-4 brightness-0 invert"
              />
              <p className="text-slate-400 text-sm">
                Administración inmobiliaria profesional en Guadalajara y área metropolitana.
              </p>
            </div>
            
            <div className="text-center">
              <h4 className="text-white font-semibold mb-4">Contacto</h4>
              <div className="space-y-3">
                <a 
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-slate-300 hover:text-emerald-400 transition-colors"
                  data-testid="footer-whatsapp"
                >
                  <Phone className="h-4 w-4" />
                  33 3191 2529
                </a>
                <a 
                  href="mailto:fernandoac@alarmen.com.mx"
                  className="flex items-center justify-center gap-2 text-slate-300 hover:text-blue-400 transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="h-4 w-4" />
                  fernandoac@alarmen.com.mx
                </a>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <Button 
                onClick={() => navigate("/login")}
                className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-2 rounded-full transition-all"
                data-testid="footer-login-button"
              >
                Acceso Clientes
              </Button>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-10 pt-8 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Rotco Propiedades. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-emerald-500 hover:bg-emerald-600 text-white p-4 rounded-full shadow-lg shadow-emerald-500/40 transition-all hover:scale-110 hover:shadow-xl"
        data-testid="floating-whatsapp-button"
      >
        <MessageCircle className="h-6 w-6" />
        <span className="absolute -top-1 -right-1 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400"></span>
        </span>
      </a>

      {/* CSS for float animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
